import { clx } from "@medusajs/ui"

import { getProductPrice } from "@lib/util/get-product-price"
import { HttpTypes } from "@medusajs/types"

export default function ProductPrice({
  product,
  variant,
}: {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
}) {
  const { cheapestPrice, variantPrice } = getProductPrice({
    product,
    variantId: variant?.id,
  })

  const selectedPrice = variant ? variantPrice : cheapestPrice

  const md = (product.metadata ?? {}) as Record<string, any>
  const pricePerSqft =
    md.pricePerSqft != null ? Number(md.pricePerSqft) : undefined
  const sqftPerBox = md.sqftPerBox != null ? Number(md.sqftPerBox) : undefined

  // Tile pricing: per-sqft headline with a sold-by-the-box subline.
  if (pricePerSqft != null && sqftPerBox != null) {
    const boxPrice = (pricePerSqft * sqftPerBox).toFixed(2)
    return (
      <div className="flex flex-col text-ink">
        <span>
          <span className="font-serif text-4xl font-semibold text-clay">
            ${pricePerSqft.toFixed(2)}
          </span>
          <span className="text-muted text-base"> / sq ft</span>
        </span>
        <p className="text-ink text-sm mt-1">
          Sold by the box · {sqftPerBox} sq ft / box · ${boxPrice} per box
        </p>
      </div>
    )
  }

  if (!selectedPrice) {
    return <div className="block w-32 h-9 bg-card animate-pulse" />
  }

  return (
    <div className="flex flex-col text-ink">
      <span
        className={clx("text-xl-semi", {
          "text-clay": selectedPrice.price_type === "sale",
        })}
      >
        {!variant && "From "}
        <span
          data-testid="product-price"
          data-value={selectedPrice.calculated_price_number}
        >
          {selectedPrice.calculated_price}
        </span>
      </span>
      {selectedPrice.price_type === "sale" && (
        <>
          <p>
            <span className="text-muted">Original: </span>
            <span
              className="line-through"
              data-testid="original-product-price"
              data-value={selectedPrice.original_price_number}
            >
              {selectedPrice.original_price}
            </span>
          </p>
          <span className="text-clay">
            -{selectedPrice.percentage_diff}%
          </span>
        </>
      )}
    </div>
  )
}
